import { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Subscription } from 'react-hook-form/dist/utils/createSubject';
import { useTranslation } from 'next-i18next';

import { checkCondition2 } from '@/shared/_legacy_/condition-utils';
import { usePrevState } from '@/shared/hooks/usePrevState';
import {
  isLabels,
  OmittedBaseDto,
  TDtoWrapper,
  ValueType,
} from '@/shared/lib/forms/form-wrapper.types';
import { UseFormRenderInputType } from '@/shared/lib/hooks/useFormRenderV2/types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { Option } from '@/shared/uikitV2/Select/types';

import { RenderQuestionComponent } from './components/RenderQuestionsComponent';
import { useCleanValuesOnDisabledIfChange } from './hooks/useCleanValuesOnDisabledIfChange';
import { renderText } from './helpers';

export const useFormRenderV2 = <
  TDto extends OmittedBaseDto,
  THelp extends { [TKey in keyof TDto]: QuestionType },
>({
  sectionItems,
  defaultValues = {},
  form,
  uploaderRef,
  validateOnMount = false,
  showRequiredOnly,
  fieldsArrayRef,
  mode,
  onHideFieldPredicate,
  highlightFieldsKeys,
}: UseFormRenderInputType<TDto, THelp>) => {
  const { t } = useTranslation(['forms_assets', 'forms_personal_ids']);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [forceReRender, setForceRerender] = useState<Record<string, unknown>>(defaultValues);
  const prevForceReRender = usePrevState(forceReRender);

  const entityType = form.watch(defaultValues).entityType;

  useEffect(() => {
    setDisabled(entityType === 'passive');
  }, [entityType]);

  useEffect(() => {
    if (validateOnMount) {
      queueMicrotask(form.trigger);
    }

    if (!sectionItems) return undefined;
    return () => {};
  }, []);

  useEffect(() => {
    if (!sectionItems) return undefined;
    let subscription: Subscription;

    const watchingItemsSet = new Set<string>();

    Object.values(sectionItems).forEach((question) => {
      if (question.showIf) {
        Object.keys(question.showIf).forEach((key) => watchingItemsSet.add(key));
      }

      if (question.disableIf) {
        Object.keys(question.disableIf).forEach((key) => watchingItemsSet.add(key));
      }

      if (question.deps) {
        question.deps.forEach((dependency: string) => watchingItemsSet.add(dependency));
      }
    });

    const watchingItems = Array.from(watchingItemsSet);

    if (watchingItems.length) {
      subscription = form.watch((data) => {
        const result = {};
        let shouldRerender = false;

        for (const item of watchingItems) {
          // @ts-ignore
          const res = data[item]?.key ? data[item].key : data[item];
          Object.assign(result, { [item]: res });
          if (res !== forceReRender[item]) {
            shouldRerender = true;
          }
        }

        if (shouldRerender) {
          setForceRerender(result);
        }
      });
    }

    return () => subscription?.unsubscribe();
  }, [forceReRender, form.watch, sectionItems]);

  useEffect(() => {
    if (highlightFieldsKeys?.length) setForceRerender(form.getValues());
  }, [highlightFieldsKeys]);

  useCleanValuesOnDisabledIfChange({
    form,
    sectionItems,
    prevForceReRender,
    forceReRender,
  });

  const Component = useMemo(() => {
    if (!sectionItems) return null;

    const formData = Object.entries(sectionItems);

    const items = formData.sort((a, b) =>
      !(typeof a[1]?.order === 'number' && !b[1]?.order)
        ? Number(a[1]?.order) - Number(b[1]?.order)
        : -100,
    );

    const renderedItems = items.map(([dtoKey, dtoValue]) => {
      if (
        dtoValue.hideCondition &&
        onHideFieldPredicate &&
        !onHideFieldPredicate(dtoValue.hideCondition)
      ) {
        return null;
      }

      if (showRequiredOnly && !dtoValue.required && dtoValue.type !== QuestionType.FILE_UPLOAD)
        return null;

      if (isLabels(dtoValue.type)) {
        const item = dtoValue;
        const shouldRender = !item.showIf || checkCondition2(forceReRender, item.showIf);
        return shouldRender ? renderText(item, dtoKey, t) : null;
      }

      const item = dtoValue as ValueType<QuestionType, TDtoWrapper<TDto, THelp>>;

      // @ts-ignore refactor checkCondition2
      const shouldRender = !item.showIf || checkCondition2(forceReRender, item.showIf);
      if (!shouldRender) return null;

      // @ts-ignore refactor checkCondition2
      const itemDisabled = item.disableIf && !checkCondition2(forceReRender, item.disableIf);
      const formValues = form.getValues();
      const shouldDisableExisting =
        mode === 'update' && item.disableIfExists && formValues[dtoKey as keyof TDto] !== undefined;
      const shouldDisable = itemDisabled || shouldDisableExisting;

      const isSelect = item.type === QuestionType.SELECT_FROM_LIST;
      const options: Option[] | undefined = isSelect
        ? item.items.map((option) => ({ value: option.key, content: { label: t(option.label) } }))
        : undefined;

      return (
        <RenderQuestionComponent
          // TODO: remove label replacing after redesign and changing all related translations
          label={t(item.label).replace(/^\*/, '')}
          key={dtoKey}
          id={dtoKey}
          question={item}
          options={options}
          uploaderRef={uploaderRef}
          fieldsArrayRef={fieldsArrayRef}
          disabled={shouldDisable}
          isHighlight={highlightFieldsKeys?.includes(dtoKey)}
        />
      );
    });

    return <FormProvider {...form}>{renderedItems}</FormProvider>;
  }, [forceReRender, t]);

  return useMemo(
    () => ({
      Component,
      sectionItems,
      disabled,
    }),
    [Component, sectionItems, disabled],
  );
};
